import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../config/api";
import LoadingIndicator from "../Components/LoadingIndicator";

const Content = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState("");
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");

  const [filterType, setFilterType] = useState("");

  const [filterValue, setFilterValue] = useState("");

  const [sort, setSort] = useState({ by: "createdAt", order: "desc" });
  const sortBy = sort.by;
  const sortOrder = sort.order;

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchContent();
  }, [currentPage, limit, search, filterType, filterValue, sortBy, sortOrder]);

  const fetchContent = async () => {
    if (!API_URL) {
      toast.error("API URL not configured.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        API_URL,
        {
          action: "getAllContent",
          page: currentPage,
          limit,

          search,

          category: filterType === "category" ? filterValue : "",

          rfqNo: filterType === "rfqNo" ? filterValue : "",

          status: filterType === "status" ? filterValue : "",

          sortBy,
          sortOrder,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      );

      const data = response.data;

      setRows(Array.isArray(data.data) ? data.data : []);

      setTotalPages(data.pagination?.totalPages || 1);

      setTotalCount(data.pagination?.totalRecords || 0);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load content";

      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
    setFilterValue("");
    setCurrentPage(1);
  };

  const handleFilterValueChange = (e) => {
    setFilterValue(e.target.value);
    setCurrentPage(1);
  };

  const applyTextFilter = () => {
    setCurrentPage(1);
    fetchContent();
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === "Enter") {
      applyTextFilter();
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearch("");
    setCurrentPage(1);
  };

  const handleClearFilterValue = () => {
    setFilterValue("");
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSort((prev) => {
      if (prev.by !== column) {
        return {
          by: column,
          order: "desc",
        };
      }

      if (prev.order === "desc") {
        return {
          by: column,
          order: "asc",
        };
      }

      return {
        by: "createdAt",
        order: "desc",
      };
    });

    setCurrentPage(1);
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) {
      return (
        <i
          className="fa-solid fa-sort"
          style={{
            fontSize: "10px",
            opacity: 0.35,
            marginLeft: "5px",
          }}
        ></i>
      );
    }

    return sortOrder === "asc" ? (
      <i
        className="fa-solid fa-sort-up"
        style={{
          fontSize: "10px",
          marginLeft: "8px",
          verticalAlign: "middle",
        }}
      ></i>
    ) : (
      <i
        className="fa-solid fa-sort-down"
        style={{
          fontSize: "10px",
          marginLeft: "8px",
          verticalAlign: "middle",
        }}
      ></i>
    );
  };

  const openDeleteModal = (row) => {
    setSelectedRow(row);
    setIsDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteOpen(false);
    setSelectedRow(null);
  };

  const handleDeleteContent = async () => {
    if (!API_URL) {
      toast.error("API URL not configured.");
      return;
    }
    const contentId = selectedRow?._id || selectedRow?.contentId;
    if (!contentId) {
      toast.error("Content ID missing.");
      return;
    }
    setIsDeleting(true);
    try {
      const response = await axios.post(
        API_URL,
        {
          action: "deleteContent",
          contentId,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      toast.success(response.data?.message || "Content deleted");
      closeDeleteModal();
      await fetchContent();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete content";
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const getCategoryLabel = (row) => {
    if (!row.category) return "-";
    if (typeof row.category === "object") return row.category.category || "-";
    return row.category;
  };
  const getRfqNo = (row) => row.rfqNo || "-";

  const getLeadVisibility = (row) => {
    if (!row.leadVisibility || row.leadVisibility.length === 0) return "-";
    return row.leadVisibility.join(", ");
  };

  const getCreatedBy = (row) => {
    const by = row.createdBy;
    if (!by) return "-";
    if (typeof by === "string") return by;
    if (typeof by === "object") return by.name || by.email || by._id || "-";
    return String(by);
  };

  const getStatusBadge = (status) => {
    if (!status) return "-";

    let className = "";

    switch (status.toLowerCase()) {
      case "active":
        className = "text-success";
        break;

      case "inactive":
        className = "text-danger";
        break;

      case "draft":
        className = "text-secondary";
        break;

      default:
        className = "text-dark";
    }

    return (
      <span className={className} style={{ fontWeight: "600" }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date
      .toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", " ");
  };

  const getRfqDueDateTime = (row) => {
    return formatDateTime(row.rfqDueDateTime);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));

    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper">
        <div className="d-flex align-items-center">
          <span className="mr-2">Show</span>

          <select
            className="form-control"
            style={{ width: "80px" }}
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <span className="ml-2 mr-3">Entries</span>

          <span className="pagination-info">
            Showing {rows.length > 0 ? (currentPage - 1) * limit + 1 : 0} to{" "}
            {Math.min(currentPage * limit, totalCount)} of {totalCount} entries
          </span>
        </div>

        <div className="pagination-controls">
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {pages.map((page) => (
            <button
              key={page}
              className={`pagination-btn ${
                currentPage === page ? "active" : ""
              }`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className="fas fa-angle-right"></i>
          </button>

          <button
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  const getRequirementSummary = (row) => {
    const summary = row.requirementSummary || "-";
    if (summary === "-" || summary.length <= 25) return summary;
    return summary.substring(0, 25) + "...";
  };

  const openSummaryModal = (summary) => {
    setSelectedSummary(summary || "-");
    setIsSummaryModalOpen(true);
  };

  const closeSummaryModal = () => {
    setIsSummaryModalOpen(false);
    setSelectedSummary("");
  };

  return (
    <div className="employee-form">
      <div className="content-header subscription-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Lead Posts</h1>
            <p>View all lead posts.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate("/content-create")}
            >
              Add Leads
            </button>
          </div>
        </div>
      </div>
      <div className="card employee-content-card">
        <div className="card-body">
          <div className="row">
            <div className="col-lg-4 col-md-6 col-sm-12 mb-3">
              <label className="font-weight-bold mb-1">Search</label>

              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  value={search}
                  onChange={handleSearchChange}
                />

                <div className="input-group-append">
                  <button
                    className="btn btn-primary"
                    onClick={handleSearchSubmit}
                  >
                    <i className="fa fa-search"></i>
                  </button>

                  {search && (
                    <button
                      className="btn btn-secondary"
                      onClick={handleClearSearch}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
              <label className="font-weight-bold mb-1">Filter</label>

              <select
                className="form-control"
                value={filterType}
                onChange={handleFilterTypeChange}
              >
                <option value="">Select</option>
                <option value="category">Category</option>
                <option value="rfqNo">RFQ No</option>
                <option value="status">Status</option>
              </select>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-6 mb-3">
              <label className="font-weight-bold mb-1">Value</label>

              {!filterType ? (
                <input
                  className="form-control"
                  placeholder="Select Filter"
                  disabled
                />
              ) : filterType === "status" ? (
                <select
                  className="form-control"
                  value={filterValue}
                  onChange={handleFilterValueChange}
                >
                  <option value="">Select Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="draft">Draft</option>
                </select>
              ) : (
                <div className="input-group">
                  <input
                    className="form-control"
                    placeholder={
                      filterType === "category"
                        ? "Enter Category"
                        : "Enter RFQ No"
                    }
                    value={filterValue}
                    onChange={handleFilterValueChange}
                    onKeyPress={handleFilterKeyPress}
                  />

                  <div className="input-group-append">
                    <button
                      className="btn btn-primary"
                      onClick={applyTextFilter}
                    >
                      <i className="fa fa-search"></i>
                    </button>

                    {filterValue && (
                      <button
                        className="btn btn-secondary"
                        onClick={handleClearFilterValue}
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : rows.length === 0 ? (
            <div className="employee-empty">No content data found.</div>
          ) : (
            <>
              <div
                className="employee-table-wrapper table-responsive"
                style={{ overflowX: "auto" }}
              >
                <table
                  className="table table-borderless employee-table subscription-table"
                  style={{ minWidth: "1000px" }}
                >
                  <thead style={{ whiteSpace: "nowrap" }}>
                    <tr>
                      <th>Sl No.</th>
                      <th
                        onClick={() => handleSort("category")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                        title="Sort by Category"
                      >
                        Category {renderSortIcon("category")}
                      </th>
                      <th
                        onClick={() => handleSort("rfqNo")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                        title="Sort by RFQ No"
                      >
                        RFQ No{renderSortIcon("rfqNo")}
                      </th>
                      <th
                        onClick={() => handleSort("rfqDueDateTime")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                        title="Sort by RFQ Due Date & Time"
                      >
                        RFQ Due Date & Time{renderSortIcon("rfqDueDateTime")}
                      </th>
                      <th>Requirement Summary</th>
                      <th>Lead Visibility</th>
                      <th
                        onClick={() => handleSort("status")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                        title="Sort by Status"
                      >
                        Status{renderSortIcon("status")}
                      </th>
                      <th
                        onClick={() => handleSort("createdBy")}
                        style={{ cursor: "pointer", userSelect: "none" }}
                        title="Sort by Created By"
                      >
                        Created By {renderSortIcon("createdBy")}
                      </th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={row._id || row.contentId || idx}>
                        <td>{(currentPage - 1) * limit + idx + 1}</td>
                        <td>{getCategoryLabel(row)}</td>
                        <td>{getRfqNo(row)}</td>
                        <td>{getRfqDueDateTime(row)}</td>
                        <td>
                          {row.requirementSummary &&
                          row.requirementSummary.length > 25 ? (
                            <span
                              style={{
                                cursor: "pointer",
                                textDecoration: "none",
                              }}
                              onClick={() =>
                                openSummaryModal(row.requirementSummary)
                              }
                              title="Click to view full summary"
                            >
                              {getRequirementSummary(row)}
                            </span>
                          ) : (
                            <span>{getRequirementSummary(row)}</span>
                          )}
                        </td>
                        <td>{getLeadVisibility(row)}</td>
                        <td>{getStatusBadge(row.status)}</td>
                        <td>{getCreatedBy(row)}</td>
                        <td>
                          <div className="employee-actions">
                            <button
                              className="action-icon-btn view"
                              type="button"
                              title="View"
                              onClick={() =>
                                navigate(
                                  `/content-view/${row._id || row.contentId}`,
                                )
                              }
                            >
                              <i className="fa-solid fa-eye"></i>
                            </button>
                            {row.status?.toLowerCase() !== "inactive" && (
                              <button
                                className="action-icon-btn edit"
                                type="button"
                                title="Edit"
                                onClick={() =>
                                  navigate(
                                    `/content-edit/${row._id || row.contentId}`,
                                  )
                                }
                              >
                                <i className="fa-solid fa-pen-to-square"></i>
                              </button>
                            )}
                            {row.status?.toLowerCase() !== "inactive" && (
                              <button
                                className="action-icon-btn delete"
                                type="button"
                                title="Delete"
                                onClick={() => openDeleteModal(row)}
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {isDeleteOpen && (
        <div className="crop-overlay">
          <div className="crop-modal confirm-modal">
            <div className="crop-header">
              <h5>Delete Content</h5>
              <button
                type="button"
                className="crop-close"
                onClick={closeDeleteModal}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <p className="confirm-message">
                Are you sure want to delete the {getRfqNo(selectedRow)} content?
              </p>
            </div>
            <div className="crop-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn confirm-delete-btn"
                onClick={handleDeleteContent}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <LoadingIndicator size={22} className="inline-loader" />
                ) : (
                  "Yes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSummaryModalOpen && (
        <div className="crop-overlay">
          <div className="crop-modal">
            <div className="crop-header">
              <h5>Requirement Summary</h5>
              <button
                type="button"
                className="crop-close"
                onClick={closeSummaryModal}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <div
                style={{
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: "1.6",
                  fontSize: "14px",
                }}
              >
                {selectedSummary}
              </div>
            </div>
            <div className="crop-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeSummaryModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Content;
